/**
 * useAuth — IdeaWorks auth state management
 * Handles JWT token storage, user session, and logout.
 * Desktop/standalone PWA mode: auto-login without credentials.
 */
import { useState, useEffect, createContext, useContext } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext(null);

function isStandaloneMode() {
  return (
    window.electronAPI?.isElectron === true ||
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true ||
    document.referrer.includes('android-app://') ||
    localStorage.getItem('ideaworks_desktop_mode') === 'true'
  );
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('ideaworks_token'));

  useEffect(() => {
    const init = async () => {
      if (token) {
        try {
          const { data } = await axios.get(`${API}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (data.authenticated) {
            setUser(data.user);
            setIsAdmin(data.is_admin || false);
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          } else {
            logout();
          }
        } catch {
          logout();
        }
      } else if (isStandaloneMode()) {
        // Desktop/PWA mode: auto-login without credentials
        try {
          const { data } = await axios.post(`${API}/api/auth/desktop-auto-login`);
          if (data.token) {
            localStorage.setItem('ideaworks_token', data.token);
            localStorage.setItem('ideaworks_user', JSON.stringify(data.user));
            setToken(data.token);
            setUser(data.user);
            setIsAdmin(data.is_admin || true);
            axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
          }
        } catch {
          // Fall through to login page
        }
      }
      setLoading(false);
    };
    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = (data) => {
    const tok = data.token;
    localStorage.setItem('ideaworks_token', tok);
    localStorage.setItem('ideaworks_user', JSON.stringify(data.user));
    setToken(tok);
    setUser(data.user);
    setIsAdmin(data.is_admin || false);
    axios.defaults.headers.common['Authorization'] = `Bearer ${tok}`;
  };

  const logout = () => {
    localStorage.removeItem('ideaworks_token');
    localStorage.removeItem('ideaworks_user');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setIsAdmin(false);
    setToken(null);
  };

  return (
    <AuthContext.Provider value={{ user, isAdmin, loading, login, logout, token }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
